<template>
    <article data-reveal class="reveal group relative flex flex-col lg:flex-row lg:items-center [animation:fade-in_linear_both] [animation-timeline:view()] [animation-range:entry_10%_cover_30%]"
        :class="index % 2 === 1 ? 'lg:flex-row-reverse' : ''"
        :style="{ '--reveal-delay': (index % 3) * 60 + 'ms' }">

        <!-- Image panel -->
        <NuxtLink :to="target"
            class="relative block w-full lg:w-[58%] aspect-[16/11] rounded-[2rem] overflow-hidden border border-app shadow-xl">
            <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title"
                class="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            <div v-else
                class="h-full w-full bg-gradient-to-br from-brand-500/20 via-primary-500/15 to-secondary-500/20 flex items-center justify-center">
                <Calendar class="h-16 w-16 text-brand-500/40" />
            </div>
            <div class="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0"></div>

            <!-- Floating date badge -->
            <div :class="[
                index % 2 === 1 ? 'left-5 right-auto md:left-auto md:right-5': 'left-5 md:right-auto'
            ]"
                class="absolute top-5 flex flex-col items-center justify-center min-w-[4rem] px-3 py-2.5 rounded-2xl bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-lg transition-transform duration-300 group-hover:-translate-y-1">
                <span class="text-brand-500 font-extrabold text-[10px] uppercase tracking-widest">{{
                    getMonth(item.startDate) || 'TBD' }}</span>
                <span class="text-header font-black text-2xl leading-none">{{ getDayNum(item.startDate) || '–' }}</span>
            </div>
        </NuxtLink>

        <!-- Overlapping content card -->
        <div class="relative z-10 w-[92%] mx-auto -mt-16 lg:mt-0 lg:w-[46%]"
            :class="index % 2 === 1 ? 'lg:-mr-16' : 'lg:-ml-16'">
            <div
                class="rounded-[1.75rem] border border-app bg-card/70 backdrop-blur-2xl p-7 lg:p-9 shadow-2xl transition-all duration-500 group-hover:-translate-y-1.5 group-hover:shadow-brand-500/10">
                <div class="flex items-center gap-2 mb-4">
                    <span :class="[
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest',
                        isLive ? 'bg-brand-500 text-white' : 'bg-muted/10  border border-app'
                    ]">
                        <span v-if="isLive" class="h-1.5 w-1.5 rounded-full bg-white animate-pulse"></span>
                        {{ item.status || 'Scheduled' }}
                    </span>
                </div>

                <h2
                    class="text-2xl lg:text-3xl font-bold text-header leading-snug mb-4 line-clamp-2 group-hover:text-brand-500 transition-colors">
                    {{ item.title }}
                </h2>
                <p class=" text-sm leading-relaxed mb-6 line-clamp-3">{{ item.description }}</p>

                <div class="space-y-2.5 mb-7">
                    <div class="flex items-center gap-3 text-sm">
                        <div class="p-1.5 rounded-md bg-brand-500/10 text-brand-500">
                            <Clock class="h-4 w-4" />
                        </div>
                        <span class="text-header font-semibold">
                            {{ formatTime(item.startDate) || 'TBA' }}
                            <template v-if="item.endDate"> – {{ formatTime(item.endDate) }}</template>
                        </span>
                    </div>
                    <div v-if="item.location" class="flex items-start gap-3 text-sm">
                        <div class="p-1.5 rounded-md bg-brand-500/10 text-brand-500">
                            <MapPin class="h-4 w-4" />
                        </div>
                        <span class="text-header font-semibold line-clamp-2 leading-snug">{{ item.location }}</span>
                    </div>
                </div>

                <NuxtLink :to="target"
                    class="inline-flex items-center gap-1.5 text-sm font-bold text-brand-500 hover:text-brand-600 transition-all group-hover:gap-2.5">
                    View details
                    <ArrowRight class="h-4 w-4" />
                </NuxtLink>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Calendar, MapPin, ArrowRight, Clock } from 'lucide-vue-next';
import { EventItem } from '~/types';

const props = defineProps<{
    item: EventItem;
    index: number;
}>();

const target = computed(() => ({
    name: 'EventSingle',
    params: { slug: props.item.slug || props.item.id },
}));

const isLive = computed(() =>
    ['upcoming', 'active', 'live', 'open'].includes((props.item.status || '').toLowerCase())
);

const getMonth = (isoDate: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('default', { month: 'short' });
};

const getDayNum = (isoDate: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : String(d.getDate());
};

const formatTime = (isoDate: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}


.reveal {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: var(--reveal-delay, 0ms);
}

.reveal.revealed {
    opacity: 1;
    transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
    .reveal {
        opacity: 1;
        transform: none;
        transition: none;
    }
}
</style>
